import { useState } from "react";
import {
  arrayOf,
  bool,
  func,
  number,
  object,
  oneOf,
  oneOfType,
  string,
} from "prop-types";
import { EstimationUnits } from "@repo/utils";
import {
  updateRateCardTeamMemberQuery,
  removeRateCardTeamMemberQuery,
} from "@query";
import { MAX_VALUES } from "@utils";
import {
  ValueCell,
  PinnableCell,
  RowGroup,
  Row,
  ExpandContainer,
} from "@tools";
import { CircularProgress, IconButton } from "@material-ui/core";
import { Trash } from "@assets";
import { Alert } from "@components";
import { useTranslation } from "react-i18next";
import { RateNameInput } from "@views/Utility/Settings/subviews/Project/subviews/Rates/components/RateNameInput";

export const RateRole = ({
  role,
  parentId,
  currency,
  companyCurrency,
  dayTime,
  americanPriceFormat,
  estimationUnit,
  onChange,
  onRemove,
  usedElementNames,
}) => {
  const { t } = useTranslation();
  
  const [ isLoading, setLoading ] = useState(false);
  const [ showRemovePrompt, setShowRemovePrompt ] = useState(false);
  
  const [ name, setName ] = useState(role.roleName);
  const [ costRate, setCostRate ] = useState(role.costRate);
  const [ salesRate, setSalesRate ] = useState(role.salesRate);

  const level = 2;

  const handleCostRateChange = async (value) => {
    try {
      const v = value / (estimationUnit === EstimationUnits.HOURS ? 1 : dayTime);
      setCostRate(v);
      await updateRateCardTeamMemberQuery(role.id, { costRate: v });
      onChange("costRate", parentId, role.id, v);
    } catch (err) {
      setCostRate(role.costRate);
    }
  };

  const handleSalesRateChange = async (value) => {
    try {
      const v = value / (estimationUnit === EstimationUnits.HOURS ? 1 : dayTime);
      setSalesRate(v);
      await updateRateCardTeamMemberQuery(role.id, { salesRate: v });
      onChange("salesRate", parentId, role.id, v);
    } catch (err) {
      setSalesRate(role.costRate);
    }
  };
  
  const handleNameChange = async (newName) => {
    if(!newName || newName === name)
      return;
    
    setName(newName);
    await updateRateCardTeamMemberQuery(role.id, { roleName: newName });
    onChange("roleName", parentId, role.id, newName);
  }

  const handleRemove = async () => {
    setShowRemovePrompt(true);
    setLoading(true);
    await removeRateCardTeamMemberQuery(role.id);
    onRemove(parentId, role.id);
    setLoading(false);
  };

  return (
    <RowGroup tableDepth={level} isStatic>
      <Row useExpandCell useDragHandleCell visible tableDepth={level}>
        <PinnableCell
          keepSmallestSize
          pinnedLeft
          tableDepth={level}
          allowOverflowDisplay
          containers={<ExpandContainer tableDepth={level} />}
        >
          <RateNameInput
            value={name}
            onAccept={handleNameChange}
            usedElementNames={usedElementNames}
            placeholder={t("common.unnamed")}
          />
        </PinnableCell>
  
        <ValueCell size="small" />
  
        <ValueCell
          size="large"
          displayContent
          editable extendPadding
          value={costRate * (estimationUnit === EstimationUnits.HOURS ? 1 : dayTime)}
          onValueChange={handleCostRateChange}
          max={estimationUnit === EstimationUnits.HOURS ? MAX_VALUES.RATE_HOUR : MAX_VALUES.RATE_DAY}
          symbolStart={companyCurrency?.symbolStart || ""}
          symbolEnd={companyCurrency?.symbolEnd || ""}
          americanFormat={americanPriceFormat}
          showDecimals
          applySuffixSpacing
        />
  
        <ValueCell
          size="large"
          displayContent
          editable extendPadding
          value={salesRate * (estimationUnit === EstimationUnits.HOURS ? 1 : dayTime)}
          onValueChange={handleSalesRateChange}
          max={estimationUnit === EstimationUnits.HOURS ? MAX_VALUES.RATE_HOUR : MAX_VALUES.RATE_DAY}
          symbolStart={currency?.symbolStart || ""}
          symbolEnd={currency?.symbolEnd || ""}
          americanFormat={americanPriceFormat}
          showDecimals
          applySuffixSpacing
        />
  
        <ValueCell size="small">
          <IconButton onClick={() => setShowRemovePrompt(true)} size="small">
            {
              isLoading
                ? <CircularProgress size={16} />
                : <Trash color="error" />
            }
          </IconButton>
        </ValueCell>
      </Row>
      
      <Alert
        open={showRemovePrompt}
        title={t("views.settings.projects.rate_card.remove_prompt.role.title")}
        onCancel={() => setShowRemovePrompt(false)}
        onAccept={handleRemove}
        acceptText={isLoading ? <CircularProgress size={16} /> : t("common.delete")}
      >
        {t("views.settings.projects.rate_card.remove_prompt.role.content")}
      </Alert>
    </RowGroup>
  );
};

RateRole.propTypes = {
  role: object.isRequired,
  parentId: oneOfType([number, string]),
  currency: object,
  companyCurrency: object,
  dayTime: number,
  americanPriceFormat: bool,
  estimationUnit: oneOf([EstimationUnits.HOURS, EstimationUnits.DAYS]).isRequired,
  onChange: func.isRequired,
  onRemove: func.isRequired,
  usedElementNames: arrayOf(string).isRequired,
};
